import React, { useState, useMemo, useEffect } from "react";
import { Grid, CircularProgress, Box, Snackbar, Alert } from "@mui/material";
import { useOrders } from "./hooks/useOrders";
import { useStatistics } from "./hooks/useStatistics";
import SummaryCard from "./components/SummaryCard";
import RevenueChart from "./components/RevenueChart";
import PaymentMethodsChart from "./components/PaymentMethodsChart";
import StaffPerformanceTable from "./components/StaffPerformanceTable";
import TopServicesChart from "./components/TopServicesChart";
import CategoryRevenueChart from "./components/CategoryRevenueChart";
import TimeRangeSelector from "./components/TimeRangeSelector";
import Header from "../../../../shared/modules/header/Header";
import Menu from "../../../../shared/modules/menu/Menu";
import { OrderData } from "./types/dashboardTypes";
import { formatDate } from "src/shared/components/dateUtils";
import { PageTitle } from "src/shared/PageTitle";
import GenerateDailyReport from "src/business-types/laundry/modules/dashboard/GenerateReport";
import { pageTitle } from "src/shared/utils/brand";

const SaloonDeshboard: React.FC = () => {
  const [timeRange, setTimeRange] = useState<string>("today");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [reportGenerated, setReportGenerated] = useState(false);

  const { orders, loading, error } = useOrders(timeRange, customStartDate, customEndDate);
  const { staffPerformance, topServices, categoryRevenue } = useStatistics(orders);

  const totals = useMemo(() => {
    const totalOrderPrice = orders.reduce((sum, order) => {
      const amt = parseFloat((order.totalAmount as any) || "0") || 0;
      return sum + amt;
    }, 0);

    const totalRevenue = orders.reduce((sumOrders, order) => {
      const paymentSum = (order.payments || []).reduce((s, p) => {
        return s + (parseFloat((p?.ReceivedAmount as any) || "0") || 0);
      }, 0);
      return sumOrders + paymentSum;
    }, 0);

    const totalOrders = orders.length;
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

    return { totalOrderPrice, totalRevenue, totalOrders, avgOrderValue };
  }, [orders]);

  const handleTimeRangeChange = (newTimeRange: string) => {
    setTimeRange(newTimeRange);
    // Reset custom dates when switching away from custom range
    if (newTimeRange !== "custom") {
      setCustomStartDate("");
      setCustomEndDate("");
    }
  };

  useEffect(() => {
    document.title = pageTitle("Salon › Deshboard");
  }, []);

  if (loading && orders.length === 0) {
    return (
      <Box className="saloon-loading-container" display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="first-saloon-deshboard-container flex flex-col h-screen overflow-hidden" style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <PageTitle title="- Dashboard" />
      {/* Fixed Header */}
      <div className="flex-shrink-0">{/* <Header Header="Dashboard" /> */}</div>

      <div className="flex flex-1 overflow-hidden">
        {/* Fixed Sidebar/Menu */}
        <div className="flex-shrink-0">{/* <Menu /> */}</div>

        {/* Scrollable Dashboard Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Loading indicator for data refresh */}
          {loading && orders.length > 0 && (
            <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: 1000 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          <Box sx={{ my: 2 }}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              {/* Left: Time Range */}
              <div className="flex-shrink-0">
                <TimeRangeSelector
                  timeRange={timeRange}
                  setTimeRange={handleTimeRangeChange}
                  customStartDate={customStartDate}
                  setCustomStartDate={setCustomStartDate}
                  customEndDate={customEndDate}
                  setCustomEndDate={setCustomEndDate}
                />
              </div>

              {/* Middle: Success message */}
              <div className="flex-1">
                {reportGenerated && (
                  <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                    The report has been sent to your email. For date-wise reports, please visit the Report tab to view or download them.
                  </div>
                )}
              </div>

              {/* Right: Button */}
              <div className="flex-shrink-0">
                <GenerateDailyReport onReportGenerated={() => setReportGenerated(true)} />
              </div>
            </div>
          </Box>

          {/* Error Snackbar */}
          <Snackbar open={!!error} autoHideDuration={6000} onClose={() => {}}>
            <Alert severity="error" sx={{ width: "100%" }}>
              {error}
            </Alert>
          </Snackbar>

          <Grid container spacing={3}>
            {/* Summary Cards */}
            <Grid item xs={12} md={4}>
              <SummaryCard
                icon="💰"
                title="Total Order Price"
                value={totals.totalOrderPrice.toFixed(2)}
                footer={
                  timeRange === "today"
                    ? "Today"
                    : timeRange === "week"
                      ? "This Week"
                      : timeRange === "month"
                        ? "This Month"
                        : timeRange === "year"
                          ? "This Year"
                          : timeRange === "custom"
                            ? `${formatDate(customStartDate)} - ${formatDate(customEndDate)}`
                            : "Overview"
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <SummaryCard icon="💵" title="Total Revenue" value={totals.totalRevenue.toFixed(2)} footer={`Avg: ₹${totals.avgOrderValue.toFixed(2)}`} />
            </Grid>

            <Grid item xs={12} md={4}>
              <SummaryCard icon="🧾" title="Total Orders" value={totals.totalOrders} footer="Orders" />
            </Grid>

            {/* Charts */}
            <Grid item xs={12} md={8}>
              <RevenueChart orders={orders as OrderData[]} timeRange={timeRange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <PaymentMethodsChart orders={orders as OrderData[]} />
            </Grid>
            <Grid item xs={12} md={6}>
              <StaffPerformanceTable staff={staffPerformance} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TopServicesChart data={topServices} />
            </Grid>
            <Grid item xs={12}>
              <CategoryRevenueChart data={categoryRevenue} />
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export default SaloonDeshboard;
